import React, { useEffect, useState } from "react";
import axios, { AxiosResponse } from 'axios';
import { shallowEqual } from 'react-redux';
import AssetsLibraryUtilities from "./AssetsLibraryUtilities";
import Engine from '../../classes/Engine';
import AssetUtility from '../../classes/AssetUtility';
import store, { StoreActions } from '../../state/store';
import config from '../../config';
import ProgressBar from "../layout/ProgressBar";
import Panel from "../layout/Panel";
import Separator from '../layout/Separator';
import { FileIOType, LayoutOrientations } from "../../globals";
import Spacer from "../layout/Spacer";
import { UploadIcon, DownloadIcon, TrashIcon, ClearIcon } from "../Iconset";
import APIUtility from "../../classes/APIUtility";
import MessagePrompter, { SpecificErrors } from "../prompts/MessagePrompter";
import NotificationPrompter from '../prompts/NotificationPrompter';
import Utils from "../../classes/Utilities";
import Scenario from '../../classes/scenarioElements/Scenario';
import ProjectLoader from '../../classes/ProjectLoader';
import { RepeatingActionsManager } from '../../classes/RepeatingActionsManager';
import AssetsManager from "../../classes/AssetsManager";
import TimeUtils from "../../classes/utilities/TimeUtils";
import RbopLoader from "../../classes/RbopLoader";
import { useTypedDispatch, useTypedSelector } from "../../state/hooks";
import { Revision } from "../../interfaces/Revision";
import AnalyticsManager from "../../classes/AnalyticsManager";
import StandbyOverlay from "../layout/StandbyOverlay";
import { IUrlForAssetUploadResponse, IVerifyAssetUploadResponse } from "../../interfaces/Responses";
import { resizeImage } from "../../classes/utilities/ImageUtils";

interface User {
	type: string;
	processed_size: number;
}

//Generators TODO: maybe not like this...
function* keyGenerator(pre)
{
	for (let i = 0; ; i++)
	{
		yield pre + "_" + i;
	}
}
const uploadKeyGenerator = keyGenerator(FileIOType.Upload);

export const allowedProjectImportTypes = new Set([
	'zip',
	'rbop'
]);

export async function refreshAssetsFromServer()
{
	//fetch the file list from the server, show 'loading' animation until the tree can is rendered - using setInterval
	try
	{
		const data = await APIUtility.getAssetsList();
		if (data)
		{
			const lastLibrary = store.getState().assetModule.library;
			if(lastLibrary)
			{
				const user = data.user as User;
				for(const [id, obj] of Object.entries( user))
				{
					if(obj.type !== 'model') continue;
					if(obj.processed_size && (!lastLibrary.user[id] || !lastLibrary.user[id].processed_size))
					{
						//show finish process
						NotificationPrompter.prompt({ content: `Uploading Model ${obj.name} Completed`, timeout: 10 });
					}
				}

				const organization = data.organization as User;
				for(const [id, obj] of Object.entries(organization))
				{
					if(obj.type !== 'model') continue;
					if(obj.processed_size && (!lastLibrary.organization[id] || !lastLibrary.organization[id].processed_size))
					{
						//show finish process
						NotificationPrompter.prompt({ content: `Uploading Model ${obj.name} Completed`, timeout: 10 });
					}
				}
			}

			store.dispatch(StoreActions.assets.setLibrary({
				// project: response.data.project,
				user: data.user,
				organization: data.organization,
			}));
			AssetsLibraryUtilities.refresh();
		}
	}
	catch(error)
	{
		console.log(error);
	}
}

export function refreshJobsFromServer()
{
	APIUtility.getModelJobs()
		.then((data) =>
		{
			if(!data || !data.jobs) return;
			
			const lastJobs = store.getState().revisionModule.revisions;
			const revisionsObject: Revision[] = [];
			for (const job of data.jobs)
			{
				const revisionObject = {
					name: job.asset.name,
					revision: job.revision_name,
					creator: job.user,
					queueOrder: job.queue_order,
					queueLength: job.queueLength,
					date: TimeUtils.formatDate(job.job_queue_date),
					startDate: TimeUtils.formatDate(job.process_start_date),
					endDate: TimeUtils.formatDate(job.process_end_date),
					status: job.process_status,
					id: job._id,
					retry_count: job.retry_count,
					error: job.error,
					errorLink: job.log_url,
					operations: job.operations,
					defaultOperations: job.default_operations && job.default_operations.length > 0 ? job.default_operations : null,
					totalPartsBefore: job.total_parts_before,
					totalPartsAfter: job.total_parts_after,
					totalPolyBefore: job.total_polycount_before,
					totalPolyAfter: job.total_polycount_after,
				}
				revisionsObject.push(revisionObject);

				if(revisionObject.status === 'success' && lastJobs && lastJobs[revisionObject.id])
				{
					if( lastJobs[revisionObject.id].status !== revisionObject.status )
					{
						NotificationPrompter.prompt({ content: `Optimization Job ${revisionObject.name} Completed`, timeout: 10 });
					}
				}
			}

			store.dispatch(StoreActions.revision.updateRevisions({ revisions: revisionsObject }));

		})
		.catch((error) =>
		{
			console.error(error);
		});
}

export function refreshExportsFromServer()
{
	const waitingForImport = store.getState().fileModule.waitingForImport;

	const rawExports = new Array<object>();
	APIUtility.getImportExportList().then(
		(data: { exports: object[], imports: {process_status: string, progress: number, error: string}[]})=>
		{
			if(!data || !data.exports) return;
			for(let i = 0; i< data.exports.length; i++)
			{
				rawExports.push(data.exports[i]) // TODO: don't save the react component itself, just the data
			}

			store.dispatch(StoreActions.files.registerExports(rawExports.reverse()));
			store.dispatch(StoreActions.files.registerImports(data.imports));

			if(waitingForImport && data?.imports?.length > 0)
			{
				if(data.imports[data.imports.length-1].process_status === 'success')
				{
					store.dispatch(StoreActions.files.setWaitingForImport(false));
					
					StandbyOverlay.updateStandbyModeMessage("Loading Scenario", true, "Please wait...");
					const scenario = Scenario.current();
					if(!scenario) throw new Error("No scenario loaded");
					APIUtility.loadSaves(scenario.id, 1)
						.then((data) =>
						{
							const save = data?.saves?.manual?.at(0);
							if(!save)
							{
								console.error("No manual save for project to load", data);

								MessagePrompter.showError(SpecificErrors.Error20005_ProjectHasNoSaves);
								return;
							}

							console.log("Loading latest manual project save...");

							AnalyticsManager.managedEvent('openScenario');
							store.dispatch(StoreActions.assets.setProjectAssets(save.assets));
							const projectLoader = new ProjectLoader();
							projectLoader.load(save.url, scenario.id);
							RepeatingActionsManager.setAction(refreshExportsFromServer, 60000);
						})
						.catch((error) =>
						{
							console.log("project load failed!");
							console.error(error);
							StandbyOverlay.stopStandbyMode();
						});
					
				}
				else if(data.imports[data.imports.length-1].process_status === 'error')
				{
					AnalyticsManager.managedEvent('openScenario');
					store.dispatch(StoreActions.files.setWaitingForImport(false));
					RepeatingActionsManager.setAction(refreshExportsFromServer, 60000);
					MessagePrompter.showError({
						code: 2103,
						title: 'Scenario import error',
						content: <div>
							{data.imports[data.imports.length-1].error}
						</div>,
					});
					StandbyOverlay.stopStandbyMode();
				}
				else if(data.imports[data.imports.length-1].progress)
				{
					StandbyOverlay.updateStandbyModeMessage("Processing", true, Math.floor(data.imports[data.imports.length-1].progress * 100 ) + ' %');
				}
			}

		}).catch((error) =>
	{
		console.error(error);
	});
}

export async function refreshRBOPMergeFromServer()
{
	const waitingForMerge = store.getState().fileModule.waitingForMerge;

	try
	{
		const data = await APIUtility.getMergeList();
			
		if(!data || !data.merges) return;
		const merges = data.merges;
		
		if(waitingForMerge && merges?.length > 0)
		{
			if(merges[merges.length-1].process_status === 'success')
			{
				store.dispatch(StoreActions.files.setWaitingForMerge(false));

				RepeatingActionsManager.removeAction(refreshRBOPMergeFromServer);

				StandbyOverlay.updateStandbyModeMessage("Merging RBOP", true, "Please wait...");
				try
				{
					const response = await fetch(merges[merges.length-1].rbopLink);
					if(response.status === 200 && response.ok)
					{
						const scenario = Scenario.current();
						if(!scenario) throw new Error("No scenario loaded");
						const realityBOPJSON = await (await response.blob()).text();
						if(realityBOPJSON) 
						{
							const objRBop = JSON.parse(realityBOPJSON);
							store.dispatch(StoreActions.assets.setProjectAssets(objRBop.asset));
							const rbopLoader = new RbopLoader(objRBop);
							await rbopLoader.load();
						} 
						else
						{
							throw new Error("RealityBOP.json not found in the zip file");
						} 
					}
					else
					{
						throw new Error("Failed to fetch the zip file");
					}
				}
				catch (error)
				{
					console.log("project load failed!");
					console.error(error);
				}
				finally
				{
					StandbyOverlay.stopStandbyMode();
				}
			}
			else if(data.merges[data.merges.length-1].process_status === 'error')
			{
				AnalyticsManager.managedEvent('openScenario');
				store.dispatch(StoreActions.files.setWaitingForMerge(false));
				RepeatingActionsManager.setAction(refreshRBOPMergeFromServer, 60000);
				MessagePrompter.showError({
					code: 2103,
					title: 'Scenario import error',
					content: <div>
						{data.merges[data.merges.length-1].error}
					</div>,
				});
				StandbyOverlay.stopStandbyMode();
			}
			else if(data.merges[data.merges.length-1].progress)
			{
				StandbyOverlay.updateStandbyModeMessage("Processing", true, Math.floor(data.merges[data.merges.length-1].progress * 100 ) + ' %');
			}
		}
	}
	catch (error)
	{
		console.log("project load failed!");
		console.error(error);
		StandbyOverlay.stopStandbyMode();	
	}
}

export default function FileManager()
{
	//redux app state
	const dispatch = useTypedDispatch();
	const filesToDownload = useTypedSelector(state => state.fileModule.downloadQueue, shallowEqual);
	const filesToUpload = useTypedSelector(state => state.fileModule.uploadQueue, shallowEqual);
	const filesProgressList = useTypedSelector(state => state.fileModule.progressList, shallowEqual);
	const shouldOpenFileUploadDialog = useTypedSelector(state => state.fileModule.fileUploadDialogOpen, shallowEqual);
	const fileManagerVisible = useTypedSelector(state => state.fileModule.fileManagerVisible, shallowEqual);
	const sessionInfo = useTypedSelector(state => state.userModule.sessionInfo, shallowEqual);
	const allowedFileTypes = sessionInfo ? new Set(sessionInfo.serverOptions['upload_formats']) : new Set([]);

	//upload dialog
	const [fileSelector, setFileSelector] = useState<HTMLInputElement>();

	function clearFileInput()
	{
		if(fileSelector)
		{
			fileSelector.value = '';
		}
	}

	//perform only once when this component mounts
	useEffect(() =>
	{
		setFileSelector(document.getElementById("file-input") as HTMLInputElement);
	}, [sessionInfo]);

	useEffect(() =>
	{
		if(!fileSelector) return;
		fileSelector.setAttribute("accept", Array.from(allowedFileTypes).map(type => { return "." + type }).join(","));
		fileSelector.onchange = (e: Event) =>
		{
			AssetsManager.registerUploads((e.currentTarget as any)?.files);
			//WC20-1591 - need to clear the file input, otherwise re-selecting the same file will not trigger onchange
			setTimeout(clearFileInput, 0);
		}

	}, [fileSelector]);

	//perform when file upload dialog should open / close
	useEffect(() =>
	{
		if (shouldOpenFileUploadDialog)
		{
			fileSelector?.click();
			dispatch(StoreActions.files.fileUploadDone());
		}
	}, [shouldOpenFileUploadDialog]);

	//perform when filesToDownload change
	useEffect(() =>
	{
		const downloadsFiles = async () => 
		{
			for (const fileId of filesToDownload)
			{
				const file = AssetsManager.getDownloadFile(fileId);
				try 
				{
					await downloadModel(file.name, file.id);
				} 
				catch(error) 
				{
					console.error(error);
				}
			}
			AssetsManager.clearDownloads();
		}
		downloadsFiles();

	}, [filesToDownload]);

	//perform when filesToUpload change
	useEffect(() =>
	{
		// filter out unsupported file formats
		const legalFiles: File[] = [];
		const illegalFiles: File[] = [];

		for (const fileId of filesToUpload)
		{
			const file = AssetsManager.getUploadFile(fileId);
			if(!file) continue;
			// taken from: https://stackoverflow.com/a/12900504/618726
			const fileExtension = Utils.getFileExtension(file.name);
			if (allowedFileTypes.has(fileExtension))
			{
				legalFiles.push(file);
			}
			else
			{
				illegalFiles.push(file);
			}
		}

		// TODO: show error with illegal files that were dropped
		if (illegalFiles.length > 0)
		{
			console.warn("illegal files:", illegalFiles);
			MessagePrompter.showError({
				...SpecificErrors.Error10001_InvalidFileType,
				content: <Panel>
					<p>
						The following files are of unsupported types:
					</p>
					<Panel className={"solid composite"}
						style={{
							maxHeight: "300px",
						}}
					>
						<ul>
							{
								illegalFiles.map((file: File, idx) =>
								{
									return (
										<li key={idx}>{file.name} ({file.type})</li>
									);
								})
							}
						</ul>
					</Panel>
					<Spacer size={{ y: "4px" }} />
					<p>
						Only the following file types may be used as assets:
					</p>
					<Panel className={"solid composite"}
						style={{
							maxHeight: "300px",
						}}
					>
						<ul>
							{
								Array.from(allowedFileTypes).map((fileType, idx) => { return <li key={idx}>{fileType}</li> })
							}
						</ul>
					</Panel>
				</Panel>
			})
		}

		uploadFiles(legalFiles);
		dispatch(StoreActions.files.clearUploads());
	}, [filesToUpload]);

	function onDownloadProgress(id, url, name, progress)
	{
		AssetsManager.registerFileLoadProgress({ 'id': id, 'url': url, 'progress': progress, 'name': name, type: FileIOType.Download });
	}

	function onDownloadComplete(id, url, name)
	{
		AssetsManager.removeFileLoadProgress(id);
	}

	function onUploadProgress(file, progress, cancelFunc)
	{
		console.log("onUploadProgress", file, progress, cancelFunc);
		AssetsManager.registerFileLoadProgress({ 'id': file.id, 'name': file.name, 'progress': progress, type: FileIOType.Upload, cancel: cancelFunc });
	}

	function onUploadComplete(file)
	{
		AssetsManager.removeFileLoadProgress(file.id);
		refreshAssetsFromServer();
	}

	async function downloadModel(assetName, assetId)
	{
		const url = AssetUtility.getProcessedAssetURL(assetId);
		try
		{
			await Engine.getInstance().createModel(
				assetId,
				url,
				assetName,
				undefined,
				onDownloadProgress,
				onDownloadComplete
			);
		}
		catch(error)
		{
			console.error(error);
		}
	}

	async function uploadFiles(fileUploads: File[])
	{
		for (const file of fileUploads)
		{			
			// force file extension to lowercase (getFileExtension returns the file name if there's no extension)
			const fileExtension = Utils.getFileExtension(file.name);
			const filename = file.name !== fileExtension ? file.name.replace(fileExtension, fileExtension.toLowerCase()) : file.name;
			let newFile = file;
			//if images resamle and reszie
			if (file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/jpg")
			{
				try 
				{
					StandbyOverlay.startStandbyMode("Processing Image", true, "Please wait...");
					//check if the image has an alpha channel
					newFile = await resizeImage(file);
				} 
				catch	
				{
					console.error("Error resizing image:", file);
					continue;
				}
				finally 
				{
					StandbyOverlay.stopStandbyMode();
				}
			}

			// step 1: get the S3 upload url
			APIUtility.getUrlForAssetUpload(filename)
				.then((value?: AxiosResponse<IUrlForAssetUploadResponse>) =>
				{
					if(!value?.data?.originalFile?.fileUploadUrl)
					{
						throw new Error("Failed to get upload URL for " + newFile.name);
					}

					// step 2: upload the file to s3
					newFile["id"] = uploadKeyGenerator.next().value as string;
					const abortController = new AbortController();

					axios.put(value.data.originalFile.fileUploadUrl, newFile, {
						headers: {
							'Authorization': undefined,
							'Content-Type': newFile.type
						},
						signal: abortController.signal,
						onUploadProgress: (progress) => onUploadProgress(newFile, progress.loaded / (progress.total || 1), () => 
						{ 
							abortController.abort();
							AssetsManager.removeFileLoadProgress(newFile["id"]);
						})
					}).then(() =>
					{
						const assetId = value.data.databaseId;

						// step 3: verify the file uploaded successfully
						APIUtility.verifyAssetUploaded(assetId)
							.then((value?: AxiosResponse<IVerifyAssetUploadResponse>) =>
							{
								if(value?.data?.originalFile?.fileUploadStatus !== "SUCCESS" || !value.data.originalFile.fileUrl)
								{
									throw new Error("Failed to verify " + newFile.name + " upload");
								}

								// step 4: asset verified, finish the process
								onUploadComplete(newFile);
								NotificationPrompter.prompt({ content: `"${newFile.name}" is now processing`, timeout: 10 });
							}).catch((error) => 
							{
								console.error(error);
								throw new Error("Failed to verify " + newFile.name + " upload");
							});
					}).catch((error) =>
					{
						console.error(error);
						throw new Error("Failed to upload " + newFile.name + " to S3");
					});
				})
				.catch((error) =>
				{
					console.error(error);
					NotificationPrompter.prompt({ content: `Failed to upload "${file.name}":\n ${error}` });
				});			
		}
	}

	function renderFiles()
	{
		const rows: JSX.Element[] = [];
		for (const fileId of Object.keys(filesProgressList))
		{
			const fileProgress = AssetsManager.getFileLoadProgress(fileId);
			if(fileProgress?.id === FileIOType.Import) continue;
			fileProgress && rows.push(
				<>
					<React.Fragment key={fileProgress.id}>
						<Panel className={"solid composite fileManagerListEntry"}
							data-name={"file-manager-entry-" + fileProgress.name}
							orientation={LayoutOrientations.Horizontal}
						>
							{
								fileProgress.type === FileIOType.Upload ? <UploadIcon /> : <DownloadIcon />
							}
							<Spacer size={{ x: "6px" }} />
							{fileProgress.name}
							<Spacer size={{ x: "6px" }} />
							<ProgressBar progress={fileProgress.progress} showLabel />
							<Spacer size={{ x: "6px" }} />
							<TrashIcon scale={0.65}
								data-name={"file-manager-cancel"}
								onClick={(e) =>
								{
									console.log("aborting", fileProgress);
									if (fileProgress.cancel)
									{
										fileProgress.cancel();
									}
									else if (fileProgress.request)
									{
										fileProgress.request.abort();
									}
								}}
							/>
						</Panel>
						<Spacer size={{ y: "2px" }} />
					</React.Fragment>
				</>
			);
		}

		return rows;
	}

	//render
	if (fileManagerVisible && filesProgressList && Object.keys(filesProgressList).length > 0)
	{
		if(Object.keys(filesProgressList).length === 1 && filesProgressList["import"]) return null; //hack to hide the file manager while importing a project
		return (
			<Panel className={"solid dark fileManagerRoot"}
				data-name={"file-manager-root"}
			>
				<div className={"fileManagerHeader largeText"}>
					File Manager
					<ClearIcon data-name={"close-file-manager-button"}
						scale={0.8}
						onClick={() => dispatch(StoreActions.files.setFileManagerVisible(false))}
					/>
				</div>
				<Spacer size={{ y: "2px" }} />
				<Separator className={"solid"}
					orientation={LayoutOrientations.Horizontal}
				/>
				<Spacer size={{ y: "2px" }} />
				<div className={"fileManagerFileList"}>
					{renderFiles()}
				</div>
			</Panel>
		);
	}

	return null;
}